

import React, { useState, useEffect, useRef, ReactNode } from 'react';
import '../styles/update-activity-modal-dark.css';
import { benchActivityService, MyActivityTaskDto } from '../services/benchActivityService';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment
} from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import { SelectChangeEvent } from '@mui/material/Select';
import { CalendarToday, Clear as ClearIcon } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { CheckCircle, Edit, Visibility, DeleteOutline } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

// Helper: Only show tooltip if text is truncated (overflowed with ellipsis)
type EllipsisTooltipProps = {
  children: ReactNode;
  title: ReactNode;
  placement?: TooltipProps['placement'];
  arrow?: boolean;
} & Omit<TooltipProps, 'title' | 'children' | 'placement' | 'arrow'>;

function EllipsisTooltip({ children, title, placement = 'top', arrow = true, ...tooltipProps }: EllipsisTooltipProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const el = spanRef.current;
    if (el && el.scrollWidth && el.clientWidth) {
      setShowTooltip(el.scrollWidth > el.clientWidth);
    }
  }, [children]);

  return (
    <>
      {showTooltip ? (
        <Tooltip title={title} placement={placement} arrow={arrow} {...tooltipProps}>
          <span
            ref={spanRef}
            style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
          >
            {children}
          </span>
        </Tooltip>
      ) : (
        <span
          ref={spanRef}
          style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
        >
          {children}
        </span>
      )}
    </>
  );
}

{/* Helper function to validate date (only allow dates from calendar) */}// Only allow dates that are present in the calendar (e.g., in the allowed week)
function isValidDate(date: string | dayjs.Dayjs | null): boolean {
  if (!date) return false;
  const regex = /^\d{2}-[A-Za-z]{3}-\d{4}$/;
  let d: dayjs.Dayjs;
  if (typeof date === 'string') {
    if (!regex.test(date)) return false;
    d = dayjs(date, 'DD-MMM-YYYY');
  } else {
    d = dayjs(date);
  }
  if (!d.isValid()) return false;
  // Get week start and end from activity.week (assume it's Monday)
  // Find activityWeekStr from props or closure
  const modal = document.querySelector('[data-activity-week]');
  let weekStr = modal?.getAttribute('data-activity-week');
  if (!weekStr) return false;
  // Accept both DD-MMM-YYYY and ISO
  let weekStart = dayjs(weekStr, ['DD-MMM-YYYY', 'YYYY-MM-DD']);
  if (!weekStart.isValid()) return false;
  // Week is Monday to Sunday
  const weekEnd = weekStart.add(6, 'day');
  // Use isSame, isAfter, isBefore for compatibility
  const isAfterOrSame = d.isAfter(weekStart, 'day') || d.isSame(weekStart, 'day');
  const isBeforeOrSame = d.isBefore(weekEnd, 'day') || d.isSame(weekEnd, 'day');
  return isAfterOrSame && isBeforeOrSame;
}



// Define ActivityRow type before use
type ActivityRow = {
  taskId?: number;
  date: string | Dayjs | null;
  comments: string;
  hours: string;
};

interface ActivityData {
  week: string;
  weekDisplay?: string;
  category: string;
  technology: string;
  name: string;
  description?: string;
  status: string;
  engagementId: number;
  activityId:number;
  startDate?: string;
}

interface UpdateActivityModalProps {
  open: boolean;
  onClose: () => void;
  activity: ActivityData;
  onSave: (data: any) => void;
  initialRows?: ActivityRow[];
}

export default function UpdateActivityModal({
  open,
  onClose,
  activity,
  onSave,
  initialRows = [],
}: UpdateActivityModalProps) {

  // Calendar popper state (must be at top of function)
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarIdx, setCalendarIdx] = useState<number | null>(null);
  const [calendarType, setCalendarType] = useState<'unsaved' | 'edit' | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<{ [key: number]: string }>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const getInitialStatus = (s: string | undefined) => {
    if (!s || s === 'yet to start') return 'In Progress';
    return s;
  };
  const [status, setStatus] = useState(getInitialStatus(activity.status));

  // Reset status to activity status when modal opens or activity changes
  useEffect(() => {
    if (open) {
      setStatus(getInitialStatus(activity.status));
    }
  }, [open, activity.status]);
  const [statusHistory, setStatusHistory] = useState(activity.status ? [activity.status] : ['yet to start']);
  const [startDate, setStartDate] = useState(activity.startDate || '');

  // Persisted records (ticked rows)
  const [rows, setRows] = useState<ActivityRow[]>(initialRows);
  useEffect(() => {
    if (open && activity.engagementId) {
      benchActivityService
        .getTasksByEngagementId(activity.engagementId)
        .then((res: any) => {
          // handle both array and {data:[]}
          const tasks = Array.isArray(res) ? res : res?.data || [];
          setRows(
            tasks
              .map((t: any) => ({
                taskId: t.taskID,
                date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
                comments: t.comments || '',
                hours: t.hours ? t.hours.toString() : ''
              }))
              .sort((a: ActivityRow, b: ActivityRow) => {
                if (!a.date) return -1;
                if (!b.date) return 1;
                const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
                const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
                return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
              })
          );
        })
        .catch((err: any) => {
          console.error("Error fetching tasks:", err);
          setRows([]);
        });
    }
    // Reset edit mode and discard unsaved edits when modal is closed or reopened
    if (!open) {
      setEditingIdx(null);
      setEditRowsMap({});
      setCommentsErrorIdx(null);
    }
  }, [open, activity.engagementId]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // Store unsaved edit rows per activity (keyed by activity.name)
  const [editRowsMap, setEditRowsMap] = useState<{ [key: string]: ActivityRow[] }>({});
  const activityKey = activity.name;
  const editRows = editRowsMap[activityKey] || [];
  const setEditRows: React.Dispatch<React.SetStateAction<ActivityRow[]>> = (valueOrUpdater) => {
    setEditRowsMap(prev => {
      const prevRows = prev[activityKey] || [];
      const newRows = typeof valueOrUpdater === 'function' ? (valueOrUpdater as (prev: ActivityRow[]) => ActivityRow[])(prevRows) : valueOrUpdater;
      return { ...prev, [activityKey]: newRows };
    });
  };

  // Error state for comments validation
  const [commentsErrorIdx, setCommentsErrorIdx] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

const handleStatusChange = (e: SelectChangeEvent<string>) => {
  const newStatus = e.target.value;
  // Prevent reverting from 'in progress' or 'completed' back to 'yet to start'
  if ((status === 'In Progress' || status === 'completed') && newStatus === 'yet to start') {
    setSnackbar({ open: true, message: 'Cannot revert to "Yet to Start" once progressed.', severity: 'error' });
    return;
  }
  setStatus(newStatus);
  setStatusHistory(prev => [...prev, newStatus]);
  if (status === "yet to start" && newStatus === "In Progress" && !startDate) {
    setStartDate(new Date().toISOString());
  }
};



  /* ---------------- ADD ROW (ALLOW MULTIPLE UNSAVED) ---------------- */
  // Add a new unsaved row (not persisted until ticked)
  const [unsavedRows, setUnsavedRows] = useState<ActivityRow[]>([]);
  // Track if user tried to add/save unsaved row (for error display)
  const [unsavedDateTouched, setUnsavedDateTouched] = useState<boolean[]>([]);
  // Track if user tried to save an edit row (for error display)
  const [editDateTouched, setEditDateTouched] = useState<{ [idx: number]: boolean }>({});

  // Reset unsavedRows when modal is opened or activity changes
  useEffect(() => {
  if (open) {
    setUnsavedRows([]);
    setUnsavedDateTouched([]);
    setRowErrors({});          // ✅ clear row errors
    setSnackbarMessage('');    // ✅ clear snackbar
  }
}, [open, activity.engagementId]);
// const getMaxDate = () => {
//   if (!rows.length) return null;

//   const latest = rows
//     .map(r => dayjs(r.date, 'DD-MMM-YYYY'))
//     .sort((a, b) => b.valueOf() - a.valueOf())[0];

//   return latest;
// };

  const handleAddRow = async () => {
    // If there is an unsaved row, try to save it first
    setRowErrors({});
    setSnackbarMessage('');
  // ❗ BLOCK ADD IF EDIT IS IN PROGRESS
if (editingIdx !== null) {
  const updatedRow = rows[editingIdx];

  // 🔴 EMPTY CHECK
  if (!updatedRow.date || !updatedRow.comments || !updatedRow.hours || updatedRow.comments.trim() === '') {
    setSnackbar({
      open: true,
      message: 'All the fields are mandatory',
      severity: 'error'
    });
    return;
  }

const isDateInvalid = !isValidDate(updatedRow.date);
  let hoursStr = updatedRow.hours.toString().trim();
  let hoursNum = Number(hoursStr);

  let isHoursInvalid =
    /^\d+\.$/.test(hoursStr) ||
    isNaN(hoursNum) ||
    hoursNum < 0.01 ||
    hoursNum > 10.0 ||
    !/^([0-9]{1,2}|0)(\.[0-9]{1,2})?$/.test(hoursStr);

  // 🔥 USE SNACKBAR ONLY (NOT rowErrors)

  if (isDateInvalid && isHoursInvalid) {
    setSnackbar({
      open: true,
      message: 'Enter valid date from calendar and valid hours',
      severity: 'error'
    });
    return;
  }

  if (isDateInvalid) {
    setSnackbar({
      open: true,
      message: 'Please enter valid date from calendar',
      severity: 'error'
    });
    return;
  }

  if (isHoursInvalid) {
    setSnackbar({
      open: true,
      message: 'Enter valid hours',
      severity: 'error'
    });
    return;
  }

try {
  const taskDto = {
    taskId: updatedRow.taskId,
taskDate: dayjs(updatedRow.date, 'DD-MMM-YYYY')
  .hour(12)   // ✅ safe midday time
  .format('YYYY-MM-DDTHH:mm:ss'),    comments: updatedRow.comments,
    hours: updatedRow.hours,
    status: status
  };

  await benchActivityService.updateTask(
  updatedRow.taskId!,              // ✅ 1st → taskId
  activity.engagementId,           // ✅ 2nd → engagementId
  taskDto                          // ✅ 3rd → data
);
  // Refresh data
  const tasks = await benchActivityService.getTasksByEngagementId(activity.engagementId);

  const taskArray: any[] = Array.isArray(tasks)
    ? tasks
    : (tasks && typeof tasks === 'object' && 'data' in tasks && Array.isArray((tasks as any).data))
      ? (tasks as any).data
      : [];

  setRows(taskArray.map((t: any) => ({
    taskId: t.taskID,
    date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
    comments: t.comments,
    hours: t.hours?.toString() || ''
  })));

  setEditingIdx(null); // ✅ exit edit mode
  setRowErrors({});

} catch (err) {
  console.log("UPDATE ERROR:", err); // 👈 VERY IMPORTANT

  let msg = 'Error while updating';
  if (err && typeof err === 'object') {
    const e = err as any;
    msg = e?.response?.data?.message || e?.message || msg;
  }

  setSnackbar({
    open: true,
    message: msg,
    severity: 'error'
  });
  return;
}
}
    if (unsavedRows.length > 0) {
      const idx = 0; // always only one unsaved row at the bottom
      const row = unsavedRows[0];
      // Validate all fields
      // 🔴 EMPTY CHECK
if (!row.date || !row.comments || !row.hours || row.comments.trim() === '') {
  setSnackbar({
    open: true,
    message: 'All the fields are mandatory',
    severity: 'error'
  });
  return;
}

const isFormatInvalid =
  typeof row.date === 'string' &&
  (!/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(row.date) ||
   /[^0-9A-Za-z-]/.test(row.date));
// 🔴 DATE VALID
const isDateInvalid = !isValidDate(row.date);
// 🔴 HOURS VALID
let hoursStr = row.hours.toString().trim();
let hoursNum = Number(hoursStr);

let isHoursInvalid =
  /^\d+\.$/.test(hoursStr) ||
  isNaN(hoursNum) ||
  hoursNum < 0.01 ||
  hoursNum > 10.0 ||
  !/^([0-9]{1,2}|0)(\.[0-9]{1,2})?$/.test(hoursStr);

// 🔴 COMBINATIONS
if (isDateInvalid && isHoursInvalid) {
  setSnackbar({
    open: true,
    message: 'Please enter valid date and hours value',
    severity: 'error'
  });
  return;
}

if (isDateInvalid) {
  setSnackbar({
    open: true,
    message: 'Please Enter valid date from calendar',
    severity: 'error'
  });
  return;
}

if (isHoursInvalid) {
  setSnackbar({
    open: true,
    message: 'Please enter a valid hours value.',
    severity: 'error'
  });
  return;
}
      try {
        if (!activity.engagementId) throw new Error('No engagementId');
        // Prevent duplicate date
        if (rows.some(r => r.date === row.date)) {
          setSnackbar({ open: true, message: 'Record already exists', severity: 'error' });
          return;
        }
        const taskDto = {
          taskDate:
  typeof row.date === 'string'
    ? dayjs(row.date, ['YYYY-MM-DD', 'DD-MMM-YYYY'])
        .hour(12)
        .format('YYYY-MM-DDTHH:mm:ss')
    : row.date
      ? row.date
          .hour(12)
          .format('YYYY-MM-DDTHH:mm:ss')
      : '',
          comments: row.comments,
          hours: row.hours,
          status: status
        };

  

        await benchActivityService.addTask(activity.engagementId, taskDto);
        // Refresh rows from backend
        const tasks = await benchActivityService.getTasksByEngagementId(activity.engagementId);
        const taskArray: any[] = Array.isArray(tasks)
          ? tasks
          : (tasks && typeof tasks === 'object' && 'data' in tasks && Array.isArray((tasks as any).data))
            ? (tasks as any).data
            : [];
        setRows(taskArray.map((t: any) => ({
          taskId: t.taskID,
          date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
          comments: t.comments,
          hours: t.hours?.toString() || ''
        })).sort((a: ActivityRow, b: ActivityRow) => {
          if (!a.date) return -1;
          if (!b.date) return 1;
          const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
          const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
          return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
        }));
        setSnackbar({ open: true, message: 'Saved!', severity: 'success' });
        setRowErrors({});
        setUnsavedRows([{ date: null, comments: '', hours: '' }]);
        setUnsavedDateTouched([false]);
        return;
      } catch (err) {
        let msg = 'Error';
        if (err && typeof err === 'object') {
          const e = err as any;
          msg = e?.response?.data?.message || e?.message || 'Error';
        }
        // Show friendly message for duplicate or future date errors
        if (typeof msg === 'string') {
          const lowerMsg = msg.toLowerCase();
          if (lowerMsg.includes('record already exists')) {
            msg = 'Record already exists';
          } else if (lowerMsg.includes('future date')) {
            msg = 'Future dates not allowed';
          }
        }
        setSnackbar({
          open: true,
          message: msg,
          severity: 'error'
        });
        return;
      }
    }
    // Add a new empty unsaved row
    setUnsavedRows([{ date: null, comments: '', hours: '' }]);
    setUnsavedDateTouched([false]);
  };

  // Utility to normalize date for duplicate check
function normalizeDate(d: string | Dayjs | null): string {
  if (!d) return '';
  if (typeof d === 'string') {
    const parsed = dayjs(d, ['DD-MMM-YYYY', 'YYYY-MM-DD']);
    return parsed.isValid() ? parsed.format('DD-MMM-YYYY').toLowerCase() : d.toLowerCase();
  }
  return dayjs(d).format('DD-MMM-YYYY').toLowerCase();
}

  /* ---------------- EDIT EXISTING ROW ---------------- */
  const handleEditRow = (idx: number) => {
    setEditingIdx(idx);
  };

  /* ---------------- SAVE SINGLE EDIT ROW ---------------- */
  // Discard (remove) an unsaved row
  const handleDiscardUnsavedRow = (idx: number) => {
    setUnsavedRows(prev => prev.filter((_, i) => i !== idx));
    setUnsavedDateTouched(prev => prev.filter((_, i) => i !== idx));
  };
  // Save ticked row: move from unsavedRows to rows, or update existing row
  const handleSaveEditRow = async (idx: number, updatedRow: ActivityRow, isUnsaved: boolean = false) => {

    setRowErrors({});
    setSnackbarMessage('');
    if (isUnsaved) {
      setUnsavedDateTouched(touched => {
        const arr = [...touched];
        arr[idx] = true;
        return arr;
      });
    } else {
      setEditDateTouched(touched => ({ ...touched, [idx]: true }));
    }

    // 🔴 EMPTY CHECK → SNACKBAR
if (!updatedRow.date || !updatedRow.comments || !updatedRow.hours || updatedRow.comments.trim() === '') {
  setSnackbar({
    open: true,
    message: 'All the fields are mandatory',
    severity: 'error'
  });
  return;
}

// 🔴 VALIDATION
const isDateInvalid = !isValidDate(updatedRow.date);

let hoursStr = updatedRow.hours.toString().trim();
let hoursNum = Number(hoursStr);

let isHoursInvalid =
  /^\d+\.$/.test(hoursStr) ||
  isNaN(hoursNum) ||
  hoursNum < 0.01 ||
  hoursNum > 10.0 ||
  !/^([0-9]{1,2}|0)(\.[0-9]{1,2})?$/.test(hoursStr);

// 🔴 ROW ERRORS (USE idx ONLY + MERGE)
if (isDateInvalid && isHoursInvalid) {
  setRowErrors({});
  setRowErrors(prev => ({
    ...prev,
    [idx]: 'Enter valid date from calendar and valid hours'
  }));
  return;
}

if (isDateInvalid) {
  setRowErrors({});
  setRowErrors(prev => ({
    ...prev,
    [idx]: 'Enter valid date from calendar'
  }));
  return;
}

if (isHoursInvalid) {
  setRowErrors({});
  setRowErrors(prev => ({
    ...prev,
    [idx]: 'Enter valid hours'
  }));
  return;
}

    try {
      if (!activity.engagementId) throw new Error('No engagementId');

      // Prevent duplicate date
      if (rows.some((r, i) => i !== idx && r.date === updatedRow.date)) {
        setSnackbar({ open: true, message: 'Record already exists', severity: 'error' });
        return;
      }

      const taskDto = {
        taskDate:
  typeof updatedRow.date === 'string'
    ? dayjs(updatedRow.date, ['YYYY-MM-DD', 'DD-MMM-YYYY'])
        .hour(12)
        .format('YYYY-MM-DDTHH:mm:ss')
    : updatedRow.date
      ? updatedRow.date
          .hour(12)
          .format('YYYY-MM-DDTHH:mm:ss')
      : '',
        comments: updatedRow.comments,
        hours: updatedRow.hours,
        status: status
      };

      if (updatedRow.taskId) {
        await benchActivityService.updateTask(updatedRow.taskId, activity.engagementId, taskDto);
      } else {
        await benchActivityService.addTask(activity.engagementId, taskDto);
      }

      // 🔥 FIX HERE (cast as any)
      const tasks: any = await benchActivityService.getTasksByEngagementId(activity.engagementId);

      setRows(tasks.map((t: any) => ({
        taskId: t.taskID,
        date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
        comments: t.comments,
        hours: t.hours?.toString() || ''
      }))
        .sort((a: ActivityRow, b: ActivityRow) => {
          if (!a.date) return -1;
          if (!b.date) return 1;
          const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
          const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
          return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
        })
      );

      setSnackbar({ open: true, message: 'Saved!', severity: 'success' });
      setRowErrors({});

    } catch (err: any) {
      let msg = err?.response?.data?.message || err.message || 'Error';
      if (typeof msg === 'string') {
        if (msg.toLowerCase().includes('record already exists')) {
          msg = 'Record already exists';
        } else if (msg.toLowerCase().includes('future dates not allowed')) {
          msg = 'Future dates not allowed';
        }
      }
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
      // Do not remove row or exit edit mode on error
      return;
    }

    // Only remove row or exit edit mode if save was successful
    if (isUnsaved) {
      setUnsavedRows(prev => prev.filter((_, i) => i !== idx));
      setUnsavedDateTouched(prev => prev.filter((_, i) => i !== idx));
    } else {
      setEditingIdx(null);
      setEditDateTouched(prev => {
        const copy = { ...prev };
        delete copy[idx];
        return copy;
      });
    }
  };

  // Delete a saved row
    async function handleDeleteSavedRow(idx: number) {
        if (!activity.engagementId) return;

        const row = rows[idx];
        if (!row.taskId) return;

        try {
            await benchActivityService.deleteTask(row.taskId);

            // 🔥 cast as any
            const tasks: any = await benchActivityService.getTasksByEngagementId(activity.engagementId);

            setRows(tasks.map((t: any) => ({
                taskId: t.taskID,
                date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
                comments: t.comments,
                hours: t.hours?.toString() || ''
              })));

            setSnackbar({ open: true, message: 'Deleted!', severity: 'success' });

        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || err.message || 'Error',
                severity: 'error'
            });
        }

        if (editingIdx === idx) setEditingIdx(null);
    }

  /* ---------------- SUBMIT ---------------- */
  // Submit: update DB only with persisted rows
    const handleSubmit = async () => {
      // Prevent submit if status is 'yet to start'
      if (status === 'yet to start') {
        setSnackbar({ open: true, message: 'Please select a valid status before submitting.', severity: 'error' });
        return;
      }
      // Check for unsaved rows with missing fields
      if (unsavedRows.length > 0) {
        const hasEmptyUnsaved = unsavedRows.some(row => !row.date || !row.comments || !row.hours);
        if (hasEmptyUnsaved) {
          setSnackbar({
            open: true,
            message: 'All the fields are mandatory',
            severity: 'error',
          });
          return;
        }
        // Validate all unsaved rows are within allowed week
        const hasInvalidDate = unsavedRows.some(row => !isValidDate(row.date));
        if (hasInvalidDate) {
          setSnackbar({
            open: true,
            message: 'All dates must be within the current activity week.',
            severity: 'error',
          });
          return;
        }
      }
      // Check for at least one row
      if (!rows.length) {
        setSnackbar({
          open: true,
          message: status === 'completed'
            ? 'Please add at least one task by changing the status to “In Progress.”'
            : 'Please add at least one task.',
          severity: 'error',
        });
        return;
      }
      // Check for empty fields in any saved row
      const hasEmpty = rows.some(row => {
        return !row.date || !row.comments || !row.hours;
      });
      if (hasEmpty) {
        setSnackbar({
          open: true,
          message: 'All the fields are mandatory',
          severity: 'error',
        });
        return;
      }
      // Validate all saved rows are within allowed week
      const hasInvalidSavedDate = rows.some(row => !isValidDate(row.date));
      if (hasInvalidSavedDate) {
        setSnackbar({
          open: true,
          message: 'All dates must be within the current activity week.',
          severity: 'error',
        });
        return;
      }
      try {
        if (activity.engagementId) {
          await benchActivityService.completeActivity(activity.engagementId);
        }
        setSnackbar({
          open: true,
          message: 'Update done successfully!',
          severity: 'success',
        });
        setTimeout(onClose, 1200);
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || err.message || 'Failed to update activity task.',
          severity: 'error',
        });
      }
    };

  // Custom onClose handler to only allow closing via close button
  const handleModalClose = (event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      // Prevent closing on backdrop or escape
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      disableEscapeKeyDown
    >
    <Box
        className="update-activity-modal"
      data-activity-week={activity.weekDisplay ? dayjs(activity.weekDisplay).format('DD-MMM-YYYY') : activity.week ? dayjs(activity.week).format('DD-MMM-YYYY') : ''}
      sx={{
        width: 820,
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: 24,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '75vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ================= HEADER ================= */}
      <Box px={2} pt={2} pb={0} display="flex" justifyContent="space-between" alignItems="center" className="modal-header">
        <Typography fontSize={19} fontWeight={700}>
          Update Activity
        </Typography>
        <IconButton onClick={onClose}>
          <span style={{ fontSize: 28 }}>&times;</span>
        </IconButton>
      </Box>

      {/* ================= INFO BAR ================= */}
      <Box
        mx={2}
        mb={1.5}
        px={2}
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className="modal-infobar"
      >
        <Box display="flex" alignItems="center" gap={1.4}>
          <Typography fontSize={12} fontWeight={600}>Week</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">
            {activity.weekDisplay
              ? dayjs(activity.weekDisplay).format('DD-MMM-YYYY')
              : activity.week
                ? dayjs(activity.week).format('DD-MMM-YYYY')
                : ''}
          </Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Category</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.category}</Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Technology</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.technology}</Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Name</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.name}</Typography>
          <Select
            size="small"
            value={status}
            onChange={handleStatusChange}
            sx={{ ml: 1.5, fontSize: 12, height: 28 }}
          >
            <MenuItem value="Yet to start" disabled={status !== 'Yet to start'}>Yet to Start</MenuItem>
            <MenuItem value="In Progress">In-Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
          {/* Show Submit button only when status is 'completed' */}
          {status === "completed" && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ minWidth: 96, background: '#124B84', fontSize: 12.5, ml: 2 }}
            >
              Submit
            </Button>
          )}
        </Box>
      </Box>

      {/* ================= TABLE ================= */}
      <TableContainer
        component={Paper}
        sx={{
          mx: 2,
          flex: 1,
          maxWidth: 'calc(100% - 32px)',
          overflowX: 'hidden',
        }}
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <TableHead>
            <TableRow sx={{ background: '#0B3A6A', '@media (prefers-color-scheme: dark)': { background: 'inherit' } }}>
              <TableCell sx={{ width: '29%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                Date
              </TableCell>
              <TableCell sx={{ width: '66%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                Comments
              </TableCell>
              <TableCell sx={{ width: '15%', fontSize: 13.5, fontWeight: 700,color: '#fff' }}>
                Hours
              </TableCell>
              <TableCell
                align="center"
                sx={{ width: '15%', fontSize: 13.5, fontWeight: 700,color: '#fff' }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Persisted records (ticked rows) */}
            {rows.map((row, idx) => (
              <React.Fragment key={row.taskId ?? `row-${idx}`}> 
              <TableRow>
                {editingIdx === idx ? (
                  <>
                    <TableCell sx={{ py: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <TextField
                          size="small"
                          value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                          placeholder="DD-MMM-YYYY"
                          onChange={e => {
                            let val = e.target.value;
                            // Only allow typing in DD-MMM-YYYY format as user types, but allow editing anywhere
                            if (/[^0-9A-Za-z-]/.test(val)) return;
                            if (val.length > 11) return;
                            // Allow partial input for user experience, but block invalid patterns
                            if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                            if (val.length === 3 && val[2] !== '-') return;
                            if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                            if (val.length === 7 && val[6] !== '-') return;
                            if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                            // If month is fully typed, check if it's a valid abbreviation
                            if (val.length >= 6) {
                              const month = val.substring(3, 6).toLowerCase();
                              const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                              if (val.length >= 6 && !validMonths.includes(month)) return;
                            }
                            // If day is fully typed, block 00 and >31
                            if (val.length >= 2) {
                              const day = val.substring(0, 2);
                              if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                            }
                            const updated = { ...row, date: val };
                            setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                          }}
                          onBlur={e => {
                            let val = e.target.value;
                            // Auto-format month part to Title Case if possible
                            if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) {
                              const day = val.substring(0,2);
                              let month = val.substring(3,6);
                              const year = val.substring(7,11);
                              month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
                              const formatted = `${day}-${month}-${year}`;
                              const updated = { ...row, date: formatted };
                              setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                            }
                          }}
                          sx={{ width: '100%', height: 36 }}
                          inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 14, padding: '0 8px', height: 36 } }}
                          error={false}
                          helperText=""
/>
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                          onClick={e => {
                            setCalendarIdx(idx);
                            setCalendarType('edit');
                            setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                            setCalendarOpen(true);
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={4}
                          value={row.comments}
                          onChange={e => {
                            let value = e.target.value;
                            if (value.length > 800) {
                              setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                              value = value.slice(0, 800);
                            }
                            const updated = { ...row, comments: value };
                            setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                          }}
                          inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                          sx={{
                            '& .MuiInputBase-input': {
                              overflowY: 'auto',
                              maxHeight: 36,
                              padding: '0 4px',
                              boxSizing: 'border-box',
                            },
                            height: 36,
                          }}
                          helperText={<span className="character-count">{`${row.comments.length}/800 characters`}</span>}
                          InputProps={{
                            endAdornment: row.comments ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  aria-label="clear comments"
                                  onClick={() => {
                                    const updated = { ...row, comments: '' };
                                    setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                                  }}
                                  edge="end"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }}
                        />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                        <TextField
                          size="small"
                          type="text"
                          value={row.hours}
                          onKeyPress={e => {
                            const char = e.key;
                            const input = e.target as HTMLInputElement;
                            const value = input.value;
                            // Allow only digits and one dot
                            if (!/[0-9.]/.test(char)) {
                              e.preventDefault();
                              return;
                            }
                            // Prevent more than one dot
                            if (char === '.' && value.includes('.')) {
                              e.preventDefault();
                              return;
                            }
                            // Simulate the next value if this key is accepted
                            let next = value.slice(0, input.selectionStart ?? 0) + char + value.slice(input.selectionEnd ?? 0);
                            if (next === '.') next = '0.';
                            // Allow leading zero if followed by dot (e.g., 0., 0.5, 0.01)
                            // Prevent multiple leading zeros (e.g., 00.5)
                            if (/^0\d/.test(next)) {
                              e.preventDefault();
                              return;
                            }
                            // Prevent more than 2 digits before decimal
                            if (!next.includes('.') && next.length > 2) {
                              e.preventDefault();
                              return;
                            }
                            // Prevent more than 2 digits after decimal
                            if (next.includes('.')) {
                              const [before, after] = next.split('.');
                              if (after.length > 2) {
                                e.preventDefault();
                                return;
                              }
                            }
                            // Accept only if next value is empty or matches 0.01–10.0 pattern
                            if (next && !/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(next)) {
                              e.preventDefault();
                              return;
                            }
                            // Accept only if next value is in range or empty
                            if (next && next !== '.' && next !== '0.' && next !== '0' && (Number(next) < 0.01 || Number(next) > 10.0)) {
                              e.preventDefault();
                              return;
                            }
                          }}
                          onChange={e => {
                            let value = e.target.value;
                            // Allow empty string (user clearing input)
                            if (value === '') {
                              const updated = { ...row, hours: '' };
                              setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                              return;
                            }
                            // Allow 0 and 0. as intermediate values so user can type 0.5, 0.01, etc.
                            if (value === '0' || value === '0.') {
                              const updated = { ...row, hours: value };
                              setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                              return;
                            }
                            // Remove leading zeros except for '0.'
                            value = value.replace(/^0+(?!\.)/, '');
                            if (value.startsWith('.')) value = '0' + value;
                            if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                              // Accept 0.01 to 10.0
                              const num = Number(value);
                              if (num >= 0.01 && num <= 10.0) {
                                const updated = { ...row, hours: value };
                                setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                              }
                            }
                          }}
                          inputProps={{ inputMode: 'decimal', style: { fontSize: 15, textAlign: 'center', padding: '0 4px', height: 36 } }}
                          sx={{ height: 36 }}
                        />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                      <Tooltip title="Save Task" arrow>
                        <IconButton onClick={() => handleSaveEditRow(idx, row)}>
                          <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton onClick={() => handleDeleteSavedRow(idx)}>
                        <DeleteOutline sx={{ fontSize: 20, color: '#d32f2f' }} />
                      </IconButton>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15 }}>
                      {row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                    </TableCell>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>
                      <EllipsisTooltip title={row.comments || ''}>
                        {row.comments}
                      </EllipsisTooltip>
                    </TableCell>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15 }}>{row.hours}</TableCell>
                    <TableCell align="center" sx={{ py: 0, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', height: 36 }}>
                      <Tooltip title="Edit Task" arrow>
                        <IconButton onClick={() => handleEditRow(idx)}>
                          <Edit sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Task" arrow>
                        <IconButton onClick={() => handleDeleteSavedRow(idx)}>
                          <DeleteOutline sx={{ fontSize: 20, color: '#d32f2f' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>

  </React.Fragment>
            ))}
  

            {/* Unsaved rows (not persisted, disappear on close/refresh) */}
            {unsavedRows.map((row, idx) => (
              <TableRow key={row.taskId ?? `unsaved-${idx}`}> 
                <TableCell sx={{ py: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <TextField
                      size="small"
                      value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                      placeholder="DD-MMM-YYYY"
                      onChange={e => {
                        let val = e.target.value;
                        if (/[^0-9A-Za-z-]/.test(val)) return;
                        if (val.length > 11) return;
                        if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                        if (val.length === 3 && val[2] !== '-') return;
                        if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                        if (val.length === 7 && val[6] !== '-') return;
                        if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                        if (val.length >= 6) {
                          const month = val.substring(3, 6).toLowerCase();
                          const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                          if (val.length >= 6 && !validMonths.includes(month)) return;
                        }
                        if (val.length >= 2) {
                          const day = val.substring(0, 2);
                          if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                        }
                        const updated = { ...row, date: val };
                        setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        setUnsavedDateTouched(prev => {
                          const arr = [...prev];
                          arr[idx] = false;
                          return arr;
                        });
                      }}
                      onBlur={e => {
                        let val = e.target.value;
                        if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) {
                          const day = val.substring(0,2);
                          let month = val.substring(3,6);
                          const year = val.substring(7,11);
                          month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
                          const formatted = `${day}-${month}-${year}`;
                          const updated = { ...row, date: formatted };
                          setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        }
                      }}
                      inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 12, padding: '0 8px', height: 36 } }}
                      sx={{ width: '100%', height: 36 }}
                      error={false}
                      helperText={null}
                      FormHelperTextProps={{ sx: { display: 'none' } }}
                    />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                      onClick={e => {
                        setCalendarIdx(idx);
                        setCalendarType('unsaved');
                        setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                        setCalendarOpen(true);
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={row.comments}
                    onChange={e => {
                      let value = e.target.value;
                      if (value.length > 800) {
                        setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                        value = value.slice(0, 800);
                      }
                      const updated = { ...row, comments: value };
                      setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                    }}
                    inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        overflowY: 'auto',
                        maxHeight: 36,
                        padding: '0 4px',
                        boxSizing: 'border-box',
                      },
                      height: 36,
                    }}
                    helperText={`${row.comments.length}/800 characters`}
                    InputProps={{
                      endAdornment: row.comments ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="clear comments"
                            onClick={() => {
                              const updated = { ...row, comments: '' };
                              setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                            }}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    type="text"
                    value={row.hours}
                    onKeyPress={e => {
                      const char = e.key;
                      const input = e.target as HTMLInputElement;
                      const value = input.value;
                      if (!/[0-9.]/.test(char)) {
                        e.preventDefault();
                        return;
                      }
                      if (char === '.' && value.includes('.')) {
                        e.preventDefault();
                        return;
                      }
                      let next = value.slice(0, input.selectionStart ?? 0) + char + value.slice(input.selectionEnd ?? 0);
                      if (next === '.') next = '0.';
                      if (/^0\d/.test(next)) {
                        e.preventDefault();
                        return;
                      }
                      if (!next.includes('.') && next.length > 2) {
                        e.preventDefault();
                        return;
                      }
                      if (next.includes('.')) {
                        const [before, after] = next.split('.');
                        if (after.length > 2) {
                          e.preventDefault();
                          return;
                        }
                      }
                      if (next && !/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(next)) {
                        e.preventDefault();
                        return;
                      }
                      if (next && next !== '.' && next !== '0.' && next !== '0' && (Number(next) < 0.01 || Number(next) > 10.0)) {
                        e.preventDefault();
                        return;
                      }
                    }}
                    onChange={e => {
                      let value = e.target.value;
                      if (value === '') {
                        const updated = { ...row, hours: '' };
                        setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        return;
                      }
                      if (value === '0' || value === '0.') {
                        const updated = { ...row, hours: value };
                        setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        return;
                      }
                      value = value.replace(/^0+(?!\.)/, '');
                      if (value.startsWith('.')) value = '0' + value;
                      if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                        const num = Number(value);
                        if (num >= 0.01 && num <= 10.0) {
                          const updated = { ...row, hours: value };
                          setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        }
                      }
                    }}
                    inputProps={{ inputMode: 'decimal', style: { fontSize: 14, textAlign: 'center', padding: '0 4px', height: 36 } }}
                    sx={{ height: 36 }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                  <Tooltip title="Save Task" arrow>
                    <IconButton onClick={() => handleSaveEditRow(idx, row, true)}>
                      <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove Task" arrow>
                    <IconButton onClick={() => handleDiscardUnsavedRow(idx)}>
                      <span style={{ fontSize: 22, fontWeight: 700, display: 'inline-block', lineHeight: 1 }}>&times;</span>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {editRows.map((row, idx) => (
              <TableRow key={row.taskId ?? `edit-${idx}`} sx={{ height: 120 }}>
                <TableCell sx={{ py: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <TextField
                      size="small"
                      value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                      placeholder="DD-MMM-YYYY"
                      onChange={e => {
                        let val = e.target.value;
                        if (/[^0-9A-Za-z-]/.test(val)) return;
                        if (val.length > 11) return;
                        if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                        if (val.length === 3 && val[2] !== '-') return;
                        if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                        if (val.length === 7 && val[6] !== '-') return;
                        if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                        if (val.length >= 6) {
                          const month = val.substring(3, 6).toLowerCase();
                          const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                          if (val.length >= 6 && !validMonths.includes(month)) return;
                        }
                        if (val.length >= 2) {
                          const day = val.substring(0, 2);
                          if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                        }
                        const r = [...editRows];
                        r[idx].date = val;
                        setEditRows(r);
                        setEditDateTouched(touched => ({ ...touched, [idx]: false }));
                      }}
                      onBlur={e => {
                        let val = e.target.value;
                        if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) {
                          const day = val.substring(0,2);
                          let month = val.substring(3,6);
                          const year = val.substring(7,11);
                          month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
                          const r = [...editRows];
                          r[idx].date = `${day}-${month}-${year}`;
                          setEditRows(r);
                        }
                      }}
                      inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 12, padding: '0 8px' } }}
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          height: 30,
                          fontSize: 14,
                          px: 1,
                          py: 0,
                        },
                        '& input': {
                          fontSize: 12,
                          padding: '0 8px',
                        },
                      }}
                      error={false}
helperText={null}
FormHelperTextProps={{ sx: { display: 'none' } }}
                      />
                    {/* Helper function to validate date (only allow dates from calendar) */}                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                      onClick={e => {
                        setCalendarIdx(idx);
                        setCalendarType('edit');
                        setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                        setCalendarOpen(true);
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  {/* Calendar Popper rendered at modal root for all edit rows */}
                </TableCell>

                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={row.comments}
                    onChange={e => {
                      let value = e.target.value;
                      if (value.length > 800) {
                        setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                        value = value.slice(0, 800);
                      }
                      const r = [...editRows];
                      r[idx].comments = value;
                      setEditRows(r);
                    }}
                    inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        overflowY: 'auto',
                        maxHeight: 36,
                        padding: '0 4px',
                        boxSizing: 'border-box',
                      },
                      height: 36,
                    }}
                    helperText={`${row.comments.length}/800 characters`}
                  />
                </TableCell>

                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    type="text"
                    value={row.hours}
                    onChange={e => {
                      let value = e.target.value;
                      // Allow empty string (user clearing input)
                      if (value === '') {
                        const r = [...editRows];
                        r[idx].hours = '';
                        setEditRows(r);
                        return;
                      }
                      // Allow 0 as first digit if followed by dot (e.g., 0.5, 0.01)
                      // Remove leading zeros except for '0.'
                      value = value.replace(/^0+(?!\.)/, '');
                      if (value.startsWith('.')) value = '0' + value;
                      if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                        // Accept 0.01 to 10.0
                        const num = Number(value);
                        if (num >= 0.01 && num <= 10.0) {
                          const r = [...editRows];
                          r[idx].hours = value;
                          setEditRows(r);
                        }
                      }
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      style: {
                        fontSize: 14,
                        textAlign: 'center',
                        padding: '0 4px',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 0,
                        fontSize: 15,
                        px: 0.5,
                        py: 0,
                      },
                    }}
                  />
                </TableCell>

                <TableCell align="center" sx={{ py: 1.5 }}>
                  <Tooltip title="Save Task" arrow>
                    <IconButton onClick={() => handleSaveEditRow(idx, editRows[idx])}>
                      <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!rows.length && !editRows.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ fontSize: 11.5, color: '#aaa' }}>
                  No tasks yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {Object.keys(rowErrors).length > 0 && (
        <Box
          className="update-activity-error-box"
          sx={{
            mx: 2,
            mt: 1,
            py: 1,
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: 13,
            fontWeight: 500,
            background: '#fdecea',
            borderRadius: 1,
          }}
        >
          {Object.values(rowErrors)[0]}
        </Box>
      )}

      {/* Move Add button below the table, only show if status is In Progress */}
      {status === "In Progress" && (
        <Box px={2} py={1} display="flex" justifyContent="flex-start">
          <Button
            onClick={handleAddRow}
            variant="contained"
            sx={{ minWidth: 96, minHeight: 20, fontSize: 12.5, background: '#124B84', '&:hover': { background: '#0B3A6A' } }}
          >
            + Add
          </Button>
        </Box>
      )}
      {/* Calendar Modal rendered at center of UpdateActivityModal */}
      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} maxWidth="xs" PaperProps={{
        className: 'update-activity-modal',
        sx: {
          borderRadius: 3,
          boxShadow: 8,
          background: 'linear-gradient(135deg, #f8fafc 60%, #e3e9f3 100%)',
          minWidth: 320,
          maxWidth: 400,
          p: 0,
        }
      }}>
        <Box position="relative">
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={pendingDate && dayjs(pendingDate, 'DD-MMM-YYYY').isValid() ? dayjs(pendingDate, 'DD-MMM-YYYY') : null}
            onChange={date => {
              const formatted = date ? dayjs(date).format('DD-MMM-YYYY') : null;
              setPendingDate(formatted);
              // Immediately update the date field for unsaved or edit row
              if (calendarIdx !== null && formatted) {
                if (calendarType === 'unsaved') {
                  setUnsavedRows(prev => prev.map((r, i) => i === calendarIdx ? { ...r, date: formatted } : r));
                } else if (calendarType === 'edit') {
                  setEditRows(prev => prev.map((r, i) => i === calendarIdx ? { ...r, date: formatted } : r));
                }
                setCalendarOpen(false);
              }
            }}
            minDate={activity.week ? dayjs(activity.week) : undefined}
            maxDate={activity.week
              ? (dayjs(activity.week).add(6, 'day').isBefore(dayjs(), 'day') ? dayjs(activity.week).add(6, 'day') : dayjs())
              : undefined}
            shouldDisableDate={date => {
              // Disable if outside the activity's week (Monday to Sunday only)
              const weekStart = activity.week ? dayjs(activity.week) : null;
              const weekEnd = weekStart ? weekStart.add(6, 'day') : null;
              const today = dayjs();
              if (weekStart && weekEnd && (date.isBefore(weekStart, 'day') || date.isAfter(weekEnd, 'day') || date.isAfter(today, 'day'))) return true;
              // Disable if already used in rows (prevent duplicate)
              const usedDates = rows.map(r => dayjs(r.date).format('YYYY-MM-DD'));
              if (pendingDate) {
                // Allow selecting the current pending date (for editing)
                if (date.format('YYYY-MM-DD') === dayjs(pendingDate, 'DD-MMM-YYYY').format('YYYY-MM-DD')) return false;
              }
              return usedDates.includes(date.format('YYYY-MM-DD'));
            }}
            slotProps={{
              actionBar: { actions: [] },
              layout: { sx: { minWidth: 320, maxWidth: 400, fontSize: 13, p: 0 } },
            }}
            sx={{
              minWidth: 320,
              maxWidth: 400,
              fontSize: 13,
              borderRadius: 2,
              boxShadow: 2,
              background: '#f4f7fb',
              p: 0,
              '.MuiPickersLayout-root': {
                minHeight: 240,
                maxHeight: 270,
                p: 0,
              },
              '.MuiPickersCalendarHeader-root': {
                background: 'rgba(25, 118, 210, 0.07)',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              },
              '.MuiPickersDay-root': {
                fontWeight: 500,
                fontSize: 12,
                borderRadius: 6,
                transition: 'background 0.2s',
              },
              '.Mui-selected': {
                background: '#1976d2 !important',
                color: '#fff !important',
              },
              '.MuiPickersDay-root:hover': {
                background: '#e3e9f3',
              },
            }}
          />
          <Box position="absolute" bottom={28} right={18} display="flex" gap={2}>
            
            <span
              style={{
                cursor: 'pointer',
                color: '#888',
                fontWeight: 500,
                fontSize: 13,
                letterSpacing: 0.2,
                padding: '2px 10px',
                borderRadius: 6,
                transition: 'background 0.2s',
              }}
              onClick={() => setCalendarOpen(false)}
              onMouseOver={e => (e.currentTarget.style.background = '#f0f0f0')}
              onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
            >Cancel</span>
          </Box>
        </Box>
      </Dialog>

      {/* ================= SUBMIT ================= */}
      {/* Submit button moved to header, see above */}

      <Snackbar
  open={snackbar.open}
  autoHideDuration={2000}
  onClose={() => setSnackbar(s => ({ ...s, open: false }))}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  sx={{ transform: 'translateY(80px)' }} 
>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  </Modal>
  );
}